/**
 * `src/lib/ui/theme/theme.ts` — resolving `ui.theme` to a rendered theme, and
 * applying it.
 *
 * THE CASCADE THIS HAS TO COOPERATE WITH (`src/app.css`)
 * ─────────────────────────────────────────────────────
 *   :root                              the complete LIGHT palette
 *   @media (prefers-color-scheme: dark)
 *     :root:not([data-theme='light'])  the dark overrides
 *   :root[data-theme='dark']           the same overrides again
 *
 * So `data-theme` wins in BOTH directions and neither direction needs a
 * JavaScript branch: `light` on a dark system is excluded by the `:not()`, and
 * `dark` on a light system is caught by the third block. `system` is the absence
 * of the attribute, which is why `applyTheme('system')` REMOVES it rather than
 * writing `data-theme="system"` — an attribute value the stylesheet has never
 * heard of would leave a dark-system user on the light palette.
 */

import type { SettingsMap } from '$lib/db/schema';

export type ThemeSetting = SettingsMap['ui.theme'];
export type ResolvedTheme = 'light' | 'dark';

export const THEME_SETTINGS: readonly ThemeSetting[] = ['system', 'light', 'dark'];

/** Arabic labels. The UI is Arabic-only (D2); there is nothing to translate. */
export const THEME_LABELS: Readonly<Record<ThemeSetting, string>> = {
	system: 'حسب النظام',
	light: 'فاتح',
	dark: 'داكن'
};

/**
 * The `<meta name="theme-color">` values, which must stay equal to `--bg` in
 * `src/app.css`. They are the browser-chrome colour on Android and the status
 * bar in an installed PWA; a mismatch shows as a seam above the page.
 *
 * `src/app.html` carries the same two literals in its two media-scoped
 * `theme-color` tags, for the first paint before any script runs. This module
 * updates them afterwards, because those tags key off `prefers-color-scheme` and
 * cannot see a MANUAL choice at all. `tests/ui/theme.spec.ts` asserts all three
 * copies agree with the stylesheet.
 */
export const THEME_COLORS: Readonly<Record<ResolvedTheme, string>> = {
	light: '#f6f5f1',
	dark: '#111a18'
};

/** Narrow an `unknown` settings value to a theme setting. Values are `unknown`
 *  at rest, and an unrecognised one must fall back rather than throw. */
export function asThemeSetting(value: unknown): ThemeSetting {
	return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
}

export interface MediaQueryLike {
	readonly matches: boolean;
}

export interface ThemeEnvironment {
	/** `window.matchMedia`, or a stub in a test. Absent in a non-DOM context. */
	matchMedia?: (query: string) => MediaQueryLike;
}

/** Does the environment prefer dark? `false` when it cannot be asked — the
 *  light palette is the one defined on bare `:root`, so it is the safe default
 *  for an SSR-less first paint and for a headless test. */
export function prefersDark(env: ThemeEnvironment): boolean {
	try {
		return env.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
	} catch {
		return false;
	}
}

/** `prefers-reduced-motion: reduce`. `app.css` already honours the media query
 *  globally; this is for the few places that animate from script and cannot. */
export function prefersReducedMotion(env: ThemeEnvironment): boolean {
	try {
		return env.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
	} catch {
		return false;
	}
}

export function resolveTheme(setting: ThemeSetting, env: ThemeEnvironment): ResolvedTheme {
	if (setting === 'light' || setting === 'dark') return setting;
	return prefersDark(env) ? 'dark' : 'light';
}

export interface ThemeTarget {
	setAttribute(name: string, value: string): void;
	removeAttribute(name: string): void;
}

export interface MetaTarget {
	getAttribute(name: string): string | null;
	setAttribute(name: string, value: string): void;
}

export interface ThemeDocument {
	readonly documentElement: ThemeTarget;
	querySelectorAll?: (selector: string) => Iterable<MetaTarget>;
}

/**
 * Apply the theme to a document. Returns the resolved theme so the caller can
 * hand it to anything that needs to know (the reader's optical compensation
 * lives in CSS, but the height cache does not care and must not be invalidated
 * by a theme change — only by a type-scale change).
 */
export function applyTheme(
	doc: ThemeDocument,
	setting: ThemeSetting,
	env: ThemeEnvironment = {}
): ResolvedTheme {
	const resolved = resolveTheme(setting, env);
	if (setting === 'system') doc.documentElement.removeAttribute('data-theme');
	else doc.documentElement.setAttribute('data-theme', setting);

	/*
	 * Keep the installed-PWA chrome in step with a MANUAL choice. The two
	 * media-scoped `theme-color` tags in `app.html` cannot do this on their own:
	 * they key off `prefers-color-scheme` and have never heard of `ui.theme`.
	 *
	 * Their `media` attributes are left ALONE and only `content` is rewritten,
	 * which is what makes this reversible. On an explicit choice both tags are
	 * given the resolved colour, so whichever one the system matches yields the
	 * same answer. Back on `system`, each tag is given the colour its own media
	 * query names and the pair resumes switching by itself. Removing or adding
	 * tags instead would depend on document order — with several theme-color
	 * tags the browser takes the FIRST whose media matches — and that is a
	 * dependency on a file this module does not own.
	 */
	const metas = doc.querySelectorAll?.('meta[name="theme-color"]');
	if (metas) {
		for (const meta of metas) {
			const media = meta.getAttribute('media') ?? '';
			const scoped = media.includes('dark') ? 'dark' : media.includes('light') ? 'light' : null;
			const colour = setting === 'system' && scoped !== null ? scoped : resolved;
			meta.setAttribute('content', THEME_COLORS[colour]);
		}
	}
	return resolved;
}
